// src/components/technician/TechnicianDashboard.jsx
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import {
  Box, Container, Typography, Paper, Stack, Button,
} from "@mui/material";

import { useAuth } from "../../contexts/AuthContext";

// ─────────────────────────────────────────────
// Palette (mirrors UserDashboard)
// ─────────────────────────────────────────────
const COLORS = {
  primary:       "#2563eb",
  primaryDark:   "#1d4ed8",
  textPrimary:   "#0f172a",
  textSecondary: "#64748b",
  border:        "#eef0f3",
  bg:            "#f6f8fb",
};

const TechnicianDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Feature cards — styled like UserDashboard
  const features = [
    { title: "Assigned Tickets", desc: "All help desk issues routed to you, in one place." },
    { title: "SLA Tracking",     desc: "Real-time visibility into due dates and overdue SLAs." },
    { title: "Status Updates",   desc: "Move tickets through Open → In Progress → Resolved." },
    { title: "Asset Context",    desc: "See linked asset tags and history for every ticket." },
    { title: "Audit Trail",      desc: "Every action logged for compliance and traceability." },
  ];

  const firstName = currentUser?.name?.split(" ")[0] || "Technician";

  return (
    <Box sx={{ bgcolor: COLORS.bg, minHeight: "100vh" }}>
      {/* ══════════ HERO ══════════ */}
      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 4, md: 6 } }}>
        <Stack alignItems="center" spacing={3}>
          {/* Logo card */}
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: 2,
              px: { xs: 3, sm: 5 },
              py: { xs: 2.5, sm: 3 },
              boxShadow: "0 4px 20px rgba(15,23,42,0.06)",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="NIYANTRIX"
              sx={{
                width: { xs: 55, sm: 70 },
                height: { xs: 55, sm: 70 },
                objectFit: "contain",
              }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: "1.8rem", sm: "2.4rem" },
                  fontWeight: 800,
                  color: "#1e3a8a",
                  letterSpacing: "1px",
                  lineHeight: 1,
                }}
              >
                NIYANTRIX
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                  fontWeight: 700,
                  color: "#1e3a8a",
                  letterSpacing: "2px",
                  mt: 0.75,
                }}
              >
                TECHNICIAN WORKSPACE
              </Typography>
            </Box>
          </Box>

          {/* Title */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: COLORS.textPrimary,
              textAlign: "center",
              fontSize: { xs: "1.6rem", sm: "2rem", md: "2.25rem" },
              mt: 2,
            }}
          >
            Hello, {firstName}
          </Typography>


          {/* Proceed CTA → Tickets */}
          <Button
            onClick={() => navigate("/technician/tickets")}
            endIcon={<ArrowRight size={18} />}
            sx={{
              mt: 2,
              bgcolor: COLORS.primary,
              color: "#fff",
              fontWeight: 700,
              fontSize: "1rem",
              textTransform: "none",
              borderRadius: 999,
              px: 4,
              py: 1.5,
              boxShadow: "0 6px 16px rgba(37,99,235,0.35)",
              "&:hover": {
                bgcolor: COLORS.primaryDark,
                boxShadow: "0 8px 20px rgba(37,99,235,0.45)",
              },
            }}
          >
            Proceed to My Tickets
          </Button>
        </Stack>
      </Container>

      {/* ══════════ FEATURE GRID ══════════ */}
      <Container maxWidth="lg" sx={{ pb: { xs: 5, md: 8 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          {features.map((feat, i) => (
            <Paper
              key={i}
              sx={{
                p: { xs: 3, sm: 3.5 },
                borderRadius: 2,
                border: `1px solid ${COLORS.border}`,
                boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
                bgcolor: "#fff",
                transition: "transform .18s ease, box-shadow .18s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "1.35rem", sm: "1.5rem" },
                  fontWeight: 700,
                  color: COLORS.textPrimary,
                  mb: 1.5,
                }}
              >
                {feat.title}
              </Typography>
              <Typography sx={{ color: COLORS.textSecondary, fontSize: "0.95rem", lineHeight: 1.5 }}>
                {feat.desc}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* ══════════ FOOTER ══════════ */}
      <Box
        component="footer"
        sx={{
          bgcolor: "#fff",
          borderTop: `1px solid ${COLORS.border}`,
          py: { xs: 4, md: 5 },
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.4rem" },
              fontWeight: 700,
              color: COLORS.textPrimary,
              mb: 1.5,
            }}
          >
            Need Help?
          </Typography>
          <Typography sx={{ color: "#475569", mb: 2, fontSize: "1rem" }}>
            Support Desk:{" "}
            <Box component="strong" sx={{ color: COLORS.textPrimary, fontWeight: 700 }}>
              +91 6360919373
            </Box>
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 0.5, sm: 1.5 }}
            justifyContent="center"
            alignItems="center"
            divider={
              <Box sx={{ display: { xs: "none", sm: "block" }, color: "#cbd5e1" }}>|</Box>
            }
          >
            {["Ticket Guidelines", "SLA Policy", "Escalate Issue"].map((label) => (
              <Typography
                key={label}
                component="span"
                sx={{
                  color: COLORS.primary,
                  fontSize: "0.95rem",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {label}
              </Typography>
            ))}
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default TechnicianDashboard;